import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "components/Button";
import { Input } from "components/Input";
import { Modal } from "components/Modal";
import { PageWithNav } from "components/PageWithNav";
import { Spinner } from "components/Spinner";
import { Switch } from "components/Switch";
import api from "utils/api";
import { useToastContext } from "context/ToastContext";

const DeleteTestModal = ({ courseId, closeModal, isOpen }) => {
  const { notify } = useToastContext();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  const confirm = async () => {
    try {
      setIsDeleting(true);
      await api.delete(`/course/${courseId}/test`);
      notify({ title: "Success", description: "Test removed!", type: "success" });
      navigate("/courses");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} title="Are you sure?" description="This will permanently delete this course test. Some users might be already doing it or have done it.">
      <div className="mt-2 flex gap-2">
        <Button size="sm" onClick={confirm} loading={isDeleting} disabled={isDeleting}>
          Confirm
        </Button>
        <Button color="secondary" size="sm" onClick={closeModal}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

export const CourseTest = () => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const { notify } = useToastContext();
  const [passRate, setPassRate] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const addQuestion = () => {
    setQuestions([...questions, { text: "", answers: [{ text: "", isCorrect: false }] }]);
  };

  const addAnswer = (questionIndex) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].answers.push({ text: "", isCorrect: false });
    setQuestions([...updatedQuestions]);
  };

  const removeQuestion = (questionIndex) => {
    const updatedQuestions = [...questions];
    const filteredQuestions = updatedQuestions.filter((__, index) => index !== questionIndex);
    setQuestions([...filteredQuestions]);
  };

  const removeAnswer = (questionIndex, answerIndex) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].answers.splice(answerIndex, 1);
    setQuestions([...updatedQuestions]);
  };

  const handleQuestionChange = (questionIndex, newText) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].text = newText;
    setQuestions([...updatedQuestions]);
  };

  const handleAnswerChange = (questionIndex, answerIndex, newText) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].answers[answerIndex].text = newText;
    setQuestions([...updatedQuestions]);
  };

  const handleSwitchChange = (questionIndex, answerIndex) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].answers.forEach((answer) => {
      answer.isCorrect = false;
    });
    updatedQuestions[questionIndex].answers[answerIndex].isCorrect = !updatedQuestions[questionIndex].answers[answerIndex].isCorrect;
    setQuestions([...updatedQuestions]);
  };

  const loadTestData = useCallback(async () => {
    try {
      setIsLoading(true);
      setHasError(null);
      const response = await api.get(`/course/${courseId}/test`);
      if (response.data?.courseTest?.questions) {
        const qcs = JSON.parse(response.data.courseTest?.questions);
        setQuestions(qcs ?? []);
      }
      setPassRate(response.data.courseTest?.pass_rate ?? 100);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [courseId]);

  const saveTest = async () => {
    try {
      setIsSubmitting(true);
      await api.post(`/course/${courseId}/test`, {
        questions,
        pass_rate: passRate,
      });
      notify({ title: "Success", description: "Test updated!", type: "success" });
      navigate("/courses");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    loadTestData();
  }, [loadTestData]);

  if (isLoading) {
    return (
      <PageWithNav>
        <div className="flex justify-center">
          <Spinner size="lg" color="primary" />
        </div>
      </PageWithNav>
    );
  }

  if (hasError) {
    return (
      <PageWithNav>
        <div className="flex justify-center text-xl font-semibold leading-3 text-red-900">There was a problem, refresh the page to try again.</div>
      </PageWithNav>
    );
  }

  return (
    <PageWithNav>
      <DeleteTestModal courseId={courseId} isOpen={isDeleteModalOpen} closeModal={() => setIsDeleteModalOpen(false)} />
      <h1 className="mb-3 pe-4 text-xl font-semibold text-neutral-900">Course test</h1>
      <Input
        className="mb-3"
        type="number"
        size="sm"
        max={100}
        min={0}
        value={passRate}
        name="pass_rate"
        onChange={(e) => setPassRate(parseInt(e.target.value))}
        label="Test pass rate %"
        placeholder="f.e 80"
      />

      {questions.map((question, questionIndex) => (
        <div key={questionIndex} className="mb-5 flex flex-col rounded-md border p-3 pt-0">
          <Input label="Question" type="text" placeholder="Enter question" value={question.text ?? ""} onChange={(e) => handleQuestionChange(questionIndex, e.target.value)} />
          <hr className="mb-2 mt-3 h-px border-0 bg-gray-200" />

          {question.answers.map((answer, answerIndex) => (
            <div key={`${answerIndex}-${questionIndex}`} className="my-2 flex items-center gap-2">
              <Input type="text" className="w-full" placeholder="Enter answer" value={answer.text ?? ""} onChange={(e) => handleAnswerChange(questionIndex, answerIndex, e.target.value)} />
              <Button size="xs" color="danger" onClick={() => removeAnswer(questionIndex, answerIndex)}>
                X
              </Button>
              <Switch isEnabled={answer.isCorrect} label="Correct" onChange={() => handleSwitchChange(questionIndex, answerIndex)} />
            </div>
          ))}
          <div className="flex gap-2">
            <Button size="xs" color="warning" className="mt-2" onClick={() => addAnswer(questionIndex)}>
              Add Answer +
            </Button>
            <Button size="xs" color="danger" className="mt-2" onClick={() => removeQuestion(questionIndex)}>
              Remove Question
            </Button>
          </div>
        </div>
      ))}
      <Button size="sm" onClick={addQuestion}>
        Add Question +
      </Button>

      <hr className="my-3 h-px border-0 bg-gray-200" />
      <div className="flex justify-between gap-2">
        <div className="flex gap-2">
          <Button size="sm" color="secondary" onClick={() => navigate("/courses")}>
            Cancel
          </Button>
          <Button disabled={isSubmitting} loading={isSubmitting} size="sm" onClick={saveTest}>
            Save
          </Button>
        </div>
        <Button color="danger" size="sm" onClick={() => setIsDeleteModalOpen(true)}>
          Delete Test
        </Button>
      </div>
    </PageWithNav>
  );
};
